import { Box, Button, InputAdornment, SelectChangeEvent, Stack, Typography } from '@mui/material'
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import { FormSectionToggleRow } from '../../form-field-components/new-and-edit/form-section-toggle-row';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Dispatch, FunctionComponent, SetStateAction, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { IPostCapitalGood } from '../../../../shared/services/capital-good/capital-good.model';
import MeasurementUnitService from '../../../../shared/services/measurement-unit/measurement-unit.service';
import GoodCategoryService from '../../../../shared/services/good-category/good-category.service';
import { IMeasurementUnit } from '../../../../shared/services/transaction/transaction.model';
import { InformationBox } from '../../miscellaneous-components/information-box';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input';
import { FormImageInputDisplay } from '../../form-field-components/new-and-edit/form-image-input';
import { FormSelectInputDisplay } from '../../form-field-components/new-and-edit/form-select-input';
import { FormRadioInputDisplay } from '../../form-field-components/new-and-edit/form-radio-input';
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons';
import { useNavigate } from 'react-router-dom';
import ManufacturerService from '../../../../shared/services/manufacturer/manufacturer.service';
import { IManufacturer } from '../../../../shared/services/manufacturer/manufacturer.model';
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service';
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { FormAutoCompleteInputDisplay } from '../../form-field-components/new-and-edit/form-autocomplete-input';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { DefraFactorDialog } from '../../product-components/defra-factor-dialog';
import { IMaterialFactor } from '../../../../shared/services/capital-good/capital-good.model';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { STAGE_FIGURE_FIELDS } from '../../../../shared/constants/product-repository';

// DEFRA GHG (activity) conversion source — the "Material use" factors live here.
const DEFRA_GHG_SOURCE_ID = '5c7f57de-5209-4851-bd5e-eb1bb0e5273c';

export const GoodForm: FunctionComponent<{
  newGood: IPostCapitalGood,
  isEditPage?: boolean,
  editField?: string,
  setEditField?: React.Dispatch<React.SetStateAction<any>>,
  onSubmit: (values: FormData) => void,
  isSubmitting: boolean | undefined,
  errorMessage?: string,
  errorCode?: number
  categories?: any
  goodActive?: boolean
  superAdmin?: boolean,
  globalGood?: boolean,
  goodInUse?: boolean,
  setErrorMessage?: Dispatch<SetStateAction<string | undefined>>,
  setErrorCode?: Dispatch<SetStateAction<number | undefined>>,
  onChooseDefra?: () => void,
  onManageEpd?: () => void,
  onFactorSourceChange?: (choice: string) => void,
}> = ({
  newGood,
  isEditPage = false,
  editField,
  setEditField,
  onSubmit,
  isSubmitting,
  errorMessage,
  errorCode,
  categories,
  goodActive,
  superAdmin = false,
  globalGood,
  goodInUse = false,
  setErrorMessage,
  setErrorCode,
  onChooseDefra,
  onManageEpd,
  onFactorSourceChange,
}) => {
    const navigate = useNavigate()
    //Services
    const measurementUnitService = new MeasurementUnitService()
    const goodCategoriesService = new GoodCategoryService()
    const manufacturerService = new ManufacturerService()
    const customFieldService = new CustomFieldService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const organisationName = useContext(UserDetailsContext).userDetails?.organisation?.name
    //State
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    const [defraOpen, setDefraOpen] = useState(false)
    const [manufacturers, setManufacturers] = useState<IManufacturer[]>([])
    const [suppliers, setSuppliers] = useState<IContactSupplierDetails[]>([])
    const sortedSuppliers = useMemo(
      () => suppliers.slice().sort((a, b) => a.name.localeCompare(b.name)),
      [suppliers]
    )
    const [imageFileData, setImageFileData] = useState<File[]>([])
    const [categoryOptions, setCategoryOptions] = useState<string[]>()
    const [subCategoryOneOptions, setSubCategorysOneOptions] = useState<string[]>()
    const [subCategoryTwoOptions, setSubCategoryTwoOptions] = useState<string[]>()
    const [hasCategoryOptions, setHasCategoryOptions] = useState<boolean | null>(null)
    const [category, setCategory] = useState<string>()
    const [subCategoryOne, setSubCategoryOne] = useState<string>()
    const [subCategoryTwo, setSubCategoryTwo] = useState<string>()
    const [categoryOnly, setCategoryOnly] = useState<boolean>(false)
    const [measurementUnits, setMeasurementUnits] = useState<IMeasurementUnit[]>()
    const [categoryAndSubCategoryOneOnly, setCategoryAndSubCategoryOneOnly] = useState<boolean>(false)

    // The edit page's good carries its linked suppliers as contactSuppliers objects;
    // the form works with their ids.
    const getInitialContactSupplierIds = () => {
      const good = newGood as any
      if (Array.isArray(good.contactSuppliers)) {
        return good.contactSuppliers.map((supplier: any) => supplier.id)
      }
      return good.contactSupplierIds || []
    }

    const getInitialCustomFieldValues = useCallback(() => {
      if (!customFields || !customFields.length || globalGood || superAdmin) return {};

      return customFields.reduce((acc, field) => {
        const matchingValue = 'customFieldValues' in newGood && newGood.customFieldValues && Array.isArray(newGood.customFieldValues)
          ? newGood.customFieldValues.find((v: any) => v.customFieldId === field.id)?.value ?? ''
          : '';
        acc[field.id] = matchingValue;
        return acc;
      }, {} as Record<string, any>);
    }, [customFields, newGood, globalGood, superAdmin]);

    //Use Effects
    useEffect(() => {
      const superAdminQuery = superAdmin ? `?superAdmin=true` : ''
      goodCategoriesService.getCategoryOptions(superAdminQuery)
        .then(response => {
          if (response.options.length > 0) {
            setHasCategoryOptions(true)
          } else {
            setHasCategoryOptions(false)
          }
          setCategoryOptions(response.options)
          if (response.goodCategoryId) {
            formikRef.current.setFieldValue('goodCategoryId', response.goodCategoryId)
          }
        })
        .catch(err => console.log(err))

      customFieldService.getAllActive(`&entryType=capitalGood`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })

      measurementUnitService.getAll()
        .then(response => {
          setMeasurementUnits(response)
        })
        .catch(err => console.log(err))

      manufacturerService.getAll(superAdminQuery)
        .then(response => {
          setManufacturers(response)
        })
        .catch(err => console.log(err))
    }, [superAdmin])

    useEffect(() => {
      if (customFields && customFields.length && newGood && !globalGood && !superAdmin) {
        const customFieldValues = getInitialCustomFieldValues();
        formikRef.current.setValues({
          name: newGood.name,
          description: newGood.description,
          goodCategoryId: newGood.goodCategoryId,
          modelNumber: newGood.modelNumber,
          kgCarbonEmission: newGood.kgCarbonEmission,
          manufacturerId: newGood.manufacturerId,
          typeOfGood: newGood.typeOfGood,
          unitType: newGood.unitType,
          unitAmount: newGood.unitAmount,
          capitalGoodReference: newGood.capitalGoodReference,
          capitalGoodImageFilePath: newGood.capitalGoodImageFilePath || '',
          sourceOfEmission: newGood.sourceOfEmission,
          stageA1A3: (newGood as any).stageA1A3 ?? null,
          stageA4: (newGood as any).stageA4 ?? null,
          stageA5: (newGood as any).stageA5 ?? null,
          stageB: (newGood as any).stageB ?? null,
          stageC: (newGood as any).stageC ?? null,
          stageD: (newGood as any).stageD ?? null,
          contactSupplierIds: getInitialContactSupplierIds(),
          ...customFieldValues,
        });
      } else {
        formikRef.current.setValues({
          name: newGood.name,
          description: newGood.description,
          goodCategoryId: newGood.goodCategoryId,
          modelNumber: newGood.modelNumber,
          kgCarbonEmission: newGood.kgCarbonEmission,
          manufacturerId: newGood.manufacturerId,
          typeOfGood: newGood.typeOfGood,
          unitType: newGood.unitType,
          unitAmount: newGood.unitAmount,
          capitalGoodReference: newGood.capitalGoodReference,
          capitalGoodImageFilePath: newGood.capitalGoodImageFilePath || '',
          sourceOfEmission: newGood.sourceOfEmission,
          stageA1A3: (newGood as any).stageA1A3 ?? null,
          stageA4: (newGood as any).stageA4 ?? null,
          stageA5: (newGood as any).stageA5 ?? null,
          stageB: (newGood as any).stageB ?? null,
          stageC: (newGood as any).stageC ?? null,
          stageD: (newGood as any).stageD ?? null,
          contactSupplierIds: getInitialContactSupplierIds(),
        });
      }

      setImageFileData([])

      const manufacturerService = new ManufacturerService()
      const superAdminQuery = superAdmin ? `?superAdmin=true` : ''
      manufacturerService.getAll(superAdminQuery)
        .then(response => {
          setManufacturers(response)
        })
        .catch(err => console.log(err))
      if (!superAdmin && !globalGood) {
        ContactSupplierService.getInstance().getActiveSuppliersLite()
          .then(response => setSuppliers(response))
          .catch(err => console.log(err))
      }
    }, [newGood, customFields, globalGood, superAdmin, getInitialCustomFieldValues])

    useEffect(() => {
      if (categories) {
        setCategory(categories.category)
        setSubCategoryOne(categories.subCategoryOne)
        setSubCategoryTwo(categories.subCategoryTwo)
      }
    }, [categories])

    useEffect(() => {
      if (isEditPage) {
        const goodCategoriesService = new GoodCategoryService()
        const superAdminQuery = superAdmin ? `&superAdmin=true` : ''
        if (category) {
          goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(category)}${superAdminQuery}`)
            .then(response => {
              setSubCategorysOneOptions(response.options)
              setCategoryOnly(!!response.goodCategoryId)
            })
            .catch(err => console.log(err))
        }
        if (category && subCategoryOne) {
          goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(category)}&subCategoryOne=${encodeURIComponent(subCategoryOne)}${superAdminQuery}`)
            .then(response => {
              setSubCategoryTwoOptions(response.options)
              setCategoryAndSubCategoryOneOnly(!!response.goodCategoryId)
            })
            .catch(err => console.log(err))
        }
      }
    }, [isEditPage, category, subCategoryOne, superAdmin])

    //Formik
    const optionalNumber = Yup.number()
      .transform((value, originalValue) => originalValue === '' || originalValue === null ? null : value)
      .nullable();

    const capitalGoodSchema = Yup.object().shape({
      name: Yup.string().required(`Please give this ${screenLabelsContext.getLabel('product')} a name`),
      description: Yup.string().nullable(),
      goodCategoryId: Yup.string().required(),
      modelNumber: Yup.string().required('You must provide a model number'),
      kgCarbonEmission: optionalNumber,
      manufacturerId: Yup.string()
        .nullable()
        .test('manufacturer-id-or-name', 'You must provide a manufacturer', function (value) {
          const { manufacturerName } = this.parent as any
          const hasId = typeof value === 'string' && value.trim().length > 0
          const hasName = typeof manufacturerName === 'string' && manufacturerName.trim().length > 0
          return hasId || hasName
        }),
      manufacturerName: Yup.string().nullable(),
      typeOfGood: Yup.string().required('You must select a type of good'),
      unitType: Yup.string().nullable(),
      unitAmount: optionalNumber,
      capitalGoodReference: Yup.string().nullable(),
      captitalGoodImageFilePath: Yup.string().nullable(),
      sourceOfEmission: Yup.string().nullable(),
      contactSupplierIds: Yup.array().of(Yup.string()).nullable(),
      ...(customFields && customFields.length > 0 && !globalGood && !superAdmin ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    }).test(
      'complete-emission-factor',
      'Provide emission amount, unit type and unit amount together, or leave all emission factor fields blank',
      (values) => {
        const valueSet = values as any;
        const hasEmissionAmount = valueSet?.kgCarbonEmission !== null && valueSet?.kgCarbonEmission !== undefined && valueSet?.kgCarbonEmission !== '';
        if (!hasEmissionAmount) return true;
        const hasUnitType = !!valueSet?.unitType;
        const hasUnitAmount = valueSet?.unitAmount !== null && valueSet?.unitAmount !== undefined && valueSet?.unitAmount !== '';
        return hasUnitType && hasUnitAmount;
      }
    )

    const isSupplierVerified = isEditPage && (newGood as any).isVerifiedBySupplier
    const deriveFactorChoice = (fs: any): string => {
      if (fs === 'library_defra') return 'defra'
      if (fs === 'supplier_epd' || fs === 'supplier_epd_provisional') return 'epd'
      return 'custom'
    }
    const [factorSourceChoice, setFactorSourceChoice] = useState<string>(deriveFactorChoice((newGood as any).factorSource))
    const [defraSourceName, setDefraSourceName] = useState<string>('DEFRA factor library')

    useEffect(() => {
      if (superAdmin || globalGood) return
      new ConversionSourceService().getAll(true, 'conversion')
        .then((sources) => {
          const defra = sources.find((s) => s.id === DEFRA_GHG_SOURCE_ID)
          if (defra?.source) setDefraSourceName(defra.source)
        })
        .catch(() => { /* keep fallback label */ })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const formik = useFormik<IPostCapitalGood & Record<string, any>>({
      initialValues: {
        name: newGood.name,
        description: newGood.description,
        goodCategoryId: newGood.goodCategoryId,
        modelNumber: newGood.modelNumber,
        kgCarbonEmission: newGood.kgCarbonEmission,
        manufacturerId: newGood.manufacturerId,
        manufacturerName: newGood.manufacturerName,
        typeOfGood: newGood.typeOfGood,
        unitType: newGood.unitType,
        // Factors are per 1 unit: the engine multiplies factor by entered units and has never
        // divided by a declared quantity, so org-level goods pin it to 1. Existing goods keep
        // their stored value (legacy per-N bases are surfaced read-only on the edit page).
        unitAmount: newGood.unitAmount ?? (!superAdmin && !globalGood ? 1 : newGood.unitAmount),
        capitalGoodReference: newGood.capitalGoodReference,
        capitalGoodImageFilePath: newGood.capitalGoodImageFilePath || '',
        sourceOfEmission: newGood.sourceOfEmission,
        factorSource: (newGood as any).factorSource ?? null,
        stageA1A3: (newGood as any).stageA1A3 ?? null,
        stageA4: (newGood as any).stageA4 ?? null,
        stageA5: (newGood as any).stageA5 ?? null,
        stageB: (newGood as any).stageB ?? null,
        stageC: (newGood as any).stageC ?? null,
        stageD: (newGood as any).stageD ?? null,
        contactSupplierIds: getInitialContactSupplierIds(),
        ...getInitialCustomFieldValues(),
      },
      validationSchema: capitalGoodSchema,
      onSubmit: (values) => {
        const requestBody = { ...values }
        if (requestBody.manufacturerId) {
          requestBody.manufacturerName = undefined
        } else {
          ; (requestBody as any).manufacturerId = null
        }
        const mutableRequestBody = requestBody as any;
        if (mutableRequestBody.kgCarbonEmission === '') mutableRequestBody.kgCarbonEmission = undefined;
        if (mutableRequestBody.unitAmount === '') mutableRequestBody.unitAmount = undefined;
        if (mutableRequestBody.unitType === '') mutableRequestBody.unitType = null;
        // Transcribed stage figures: blank means not stated, never zero.
        for (const stageField of ['stageA1A3', 'stageA4', 'stageA5', 'stageB', 'stageC', 'stageD']) {
          if (mutableRequestBody[stageField] === '' || mutableRequestBody[stageField] === undefined) mutableRequestBody[stageField] = null;
        }
        const hasEmissionFactor = mutableRequestBody.kgCarbonEmission !== null && mutableRequestBody.kgCarbonEmission !== undefined && mutableRequestBody.kgCarbonEmission !== '';
        if (!hasEmissionFactor) {
          mutableRequestBody.kgCarbonEmission = null;
          mutableRequestBody.factorSource = null;
          // Keep the unit for an EPD-sourced product (no factor yet — the EPD supplies it after save,
          // and its declared unit must match the product's unit).
          if (factorSourceChoice !== 'epd') {
            mutableRequestBody.unitAmount = null;
            mutableRequestBody.unitType = null;
          }
        }

        const customFieldValues = Array.isArray(customFields) && !globalGood && !superAdmin
          ? customFields.map((field) => ({
            customFieldId: field.id,
            value: values?.[field.id] ?? null,
          }))
          : []

        const form = new FormData();
        for (let i = 0; i < imageFileData.length; i++) {
          form.append("files", imageFileData[i]);
        }
        form.append('capitalGood', JSON.stringify({
          ...requestBody,
          customFieldValues,
        }));
        onSubmit(form)
      }
    })

    const formikRef = useRef(formik);
    formikRef.current = formik;

    //Form Event Handlers
    const selectCategoryHandler = (e: SelectChangeEvent<string>) => {
      if (e.target.value !== category) {
        setSubCategoryOne('')
        setSubCategorysOneOptions([])
        setSubCategoryTwo('')
        setSubCategoryTwoOptions([])
        formik.setFieldValue('goodCategoryId', '')
        setCategoryOnly(false)
      }
      setCategory(e.target.value)
      const superAdminQuery = superAdmin ? `&superAdmin=true` : ''
      goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(e.target.value)}${superAdminQuery}`)
        .then(response => {
          setSubCategorysOneOptions(response.options)
          if (response.goodCategoryId) {
            formik.setFieldValue('goodCategoryId', response.goodCategoryId)
            setCategoryOnly(true)
          } else {
            setCategoryOnly(false)
          }
        })
        .catch(err => console.log(err))
    }

    const selectSubCategoryOneHandler = (e: SelectChangeEvent<string>) => {
      if (e.target.value !== subCategoryOne) {
        setSubCategoryTwo('')
        setSubCategoryTwoOptions([])
        formik.setFieldValue('goodCategoryId', '')
        setCategoryAndSubCategoryOneOnly(false)
      }
      setSubCategoryOne(e.target.value)
      if (category) {
        const superAdminQuery = superAdmin ? `&superAdmin=true` : ''
        goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(category)}&subCategoryOne=${encodeURIComponent(e.target.value)}${superAdminQuery}`)
          .then(response => {
            setSubCategoryTwoOptions(response.options)
            if (response.goodCategoryId) {
              formik.setFieldValue('goodCategoryId', response.goodCategoryId)
              setCategoryAndSubCategoryOneOnly(true)
            } else {
              setCategoryAndSubCategoryOneOnly(false)
            }
          })
          .catch(err => console.log(err))
      }
    }

    const selectSubCategoryTwoHandler = (e: SelectChangeEvent<string>) => {
      if (e.target.value !== subCategoryTwo) {
        formik.setFieldValue('goodCategoryId', '')
      }
      setSubCategoryTwo(e.target.value)
      if (category && subCategoryOne) {
        const superAdminQuery = superAdmin ? `&superAdmin=true` : ''
        goodCategoriesService.getCategoryOptions(`?category=${encodeURIComponent(category)}&subCategoryOne=${encodeURIComponent(subCategoryOne)}&subCategoryTwo=${encodeURIComponent(e.target.value)}${superAdminQuery}`)
          .then(response => {
            formik.setFieldValue('goodCategoryId', response.goodCategoryId)
          })
          .catch(err => console.log(err))
      }
    }

    // A sold product's manufacturer is usually the organisation itself, so picking the
    // sold type defaults an EMPTY manufacturer to it (existing record when one matches by
    // name, free-text otherwise). A default only: never overwrites a chosen manufacturer,
    // and stays editable for white-label or contract-manufactured ranges.
    const typeOfGoodHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.handleChange(e)
      if (e.target.value === 'soldProduct' && organisationName && !formik.values.manufacturerId && !formik.values.manufacturerName) {
        const orgManufacturer = manufacturers.find(m => m.name.trim().toLowerCase() === organisationName.trim().toLowerCase())
        if (orgManufacturer) {
          formik.setFieldValue('manufacturerId', orgManufacturer.id)
          formik.setFieldValue('manufacturerName', '')
        } else {
          formik.setFieldValue('manufacturerId', null)
          formik.setFieldValue('manufacturerName', organisationName)
        }
      }
    }

    const handleManufacturerChange = (event: any, newValue: any) => {
      if (typeof newValue === 'string') {
        formik.setFieldValue('manufacturerId', null)
        formik.setFieldValue('manufacturerName', newValue)
      } else if (newValue) {
        formik.setFieldValue('manufacturerId', newValue.id)
        formik.setFieldValue('manufacturerName', '')
      } else {
        formik.setFieldValue('manufacturerId', null)
        formik.setFieldValue('manufacturerName', '')
      }
    }

    const handleSuppliersChange = (event: any, newValue: any) => {
      const ids = Array.isArray(newValue)
        ? newValue.map((option: any) => (typeof option === 'string' ? option : option?.id)).filter(Boolean)
        : []
      formik.setFieldValue('contactSupplierIds', ids)
    }

    const handleManufacturerInputChange = (event: any, newInputValue: string) => {
      if (editField === 'manufacturerId' || !isEditPage) {
        formik.setFieldValue('manufacturerId', null)
        formik.setFieldValue('manufacturerName', newInputValue)
      }
    }

    const cancelEditHandler = () => {
      if (setEditField) {
        setEditField('')
      }
      formik.setTouched({})
      formik.setErrors({})
      if (setErrorMessage) {
        setErrorMessage(undefined)
      }
      if (setErrorCode) {
        setErrorCode(undefined)
      }
      setImageFileData([])
      if (newGood) {
        const originalCustomFieldValues = customFields?.reduce((acc, field) => {
          const originalValue = isEditPage && 'customFieldValues' in newGood && (newGood as any).customFieldValues
            ? (newGood as any).customFieldValues.find((v: any) => v.customFieldId === field.id)?.value ?? ''
            : '';
          acc[field.id] = originalValue;
          return acc;
        }, {} as Record<string, any>) || {};

        formik.setValues({
          name: newGood.name,
          description: newGood.description,
          goodCategoryId: newGood.goodCategoryId,
          modelNumber: newGood.modelNumber,
          kgCarbonEmission: newGood.kgCarbonEmission,
          manufacturerId: newGood.manufacturerId,
          manufacturerName: newGood.manufacturerName,
          typeOfGood: newGood.typeOfGood,
          unitType: newGood.unitType,
          unitAmount: newGood.unitAmount,
          capitalGoodReference: newGood.capitalGoodReference,
          capitalGoodImageFilePath: newGood.capitalGoodImageFilePath || '',
          sourceOfEmission: newGood.sourceOfEmission,
          contactSupplierIds: getInitialContactSupplierIds(),
          ...originalCustomFieldValues,
        })
      }
    }


    const uploadGoodImageHandler = () => {
      if (imageFileData.length === 0) return;
      const previewUrl = URL.createObjectURL(imageFileData[0]);
      formik.setFieldValue('capitalGoodImageFilePath', previewUrl);
    }

    const handleDefraFactorSelect = (factor: IMaterialFactor) => {
      formik.setFieldValue('kgCarbonEmission', factor.multiplier)
      formik.setFieldValue('unitType', factor.measurementUnitId)
      formik.setFieldValue('unitAmount', 1)
      formik.setFieldValue('sourceOfEmission', `DEFRA: ${factor.label}${factor.calendarYear ? ` (${factor.calendarYear})` : ''}`)
      formik.setFieldValue('factorSource', 'library_defra')
    }

    const openDefraPicker = () => { if (onChooseDefra) { onChooseDefra() } else { setDefraOpen(true) } }
    const handleSourceChange = (choice: string) => {
      setFactorSourceChoice(choice)
      onFactorSourceChange?.(choice)
      // A factor value belongs to the source that produced it. When the new choice no longer
      // matches the stored provenance, clear the value and its description rather than letting
      // a library number masquerade as a custom or EPD figure (or vice versa).
      const storedSource = (formik.values as any).factorSource
      const matchesChoice =
        (choice === 'defra' && storedSource === 'library_defra') ||
        (choice === 'epd' && (storedSource === 'supplier_epd' || storedSource === 'supplier_epd_provisional')) ||
        (choice === 'custom' && !storedSource)
      if (!matchesChoice) {
        formik.setFieldValue('factorSource', null)
        formik.setFieldValue('sourceOfEmission', '')
        formik.setFieldValue('kgCarbonEmission', '')
      }
      if (choice === 'defra' && !matchesChoice) {
        openDefraPicker()
      } else if (choice === 'epd' && onManageEpd) {
        onManageEpd()
      }
      // Collapse the source row back to its display state (matches the other inline-edit fields).
      if (setEditField) setEditField('')
    }

    // The stage-figure section starts collapsed (most products never record module values) and
    // opens itself when the product already carries any, so recorded data is never hidden.
    const [viewStageFigures, setViewStageFigures] = useState<boolean>(() =>
      STAGE_FIGURE_FIELDS.some(({ field }) => (newGood as any)[field] !== null && (newGood as any)[field] !== undefined && (newGood as any)[field] !== ''))
    const recordedStageCount = STAGE_FIGURE_FIELDS.filter(({ field }) => {
      const value = (formik.values as any)[field]
      return value !== null && value !== undefined && value !== ''
    }).length

    // Custom fields fold the same way. They load async, so the initial state is decided when
    // they arrive: open when any is mandatory (its validation error must be visible) or any
    // already holds a value; collapsed otherwise.
    const [viewCustomFields, setViewCustomFields] = useState<boolean>(false)
    useEffect(() => {
      if (!customFields || customFields.length === 0) return
      const hasMandatory = customFields.some((field) => field.isMandatory)
      const storedValues: { customFieldId: string, value: string | null }[] = (newGood as any).customFieldValues || []
      const hasValue = storedValues.some((stored) => stored.value !== null && stored.value !== undefined && stored.value !== '')
      setViewCustomFields(hasMandatory || hasValue)
    }, [customFields, newGood])
    const recordedCustomFieldCount = !customFields ? 0 : customFields.filter((field) => {
      const value = (formik.values as any)[field.id]
      return value !== null && value !== undefined && value !== ''
    }).length

    // Border bookkeeping for the folding sections: the last VISIBLE element of the bordered
    // list carries the bottom border and rounded corners.
    const hasStageSection = !globalGood && !superAdmin
    const hasCustomFieldsSection = !globalGood && !superAdmin && !!customFields && customFields.length > 0

    // Transcribed data warns, never blocks: flag an A1-A3 module value that disagrees with the
    // headline cradle-to-gate figure, but let the user save exactly what their report states.
    const stageA1A3Value = Number((formik.values as any).stageA1A3)
    const headlineValue = Number(formik.values.kgCarbonEmission)
    const stageA1A3MismatchWarning = (formik.values as any).stageA1A3 !== null && (formik.values as any).stageA1A3 !== '' && !Number.isNaN(stageA1A3Value)
      && formik.values.kgCarbonEmission !== null && formik.values.kgCarbonEmission !== undefined && String(formik.values.kgCarbonEmission) !== '' && !Number.isNaN(headlineValue)
      && Math.abs(stageA1A3Value - headlineValue) > Math.max(Math.abs(headlineValue) * 0.005, 0.0001)
      ? `Differs from the Emissions per Unit figure (${headlineValue}). That can be correct (for example a cradle-to-gate figure from a different report), so this is a note, not an error.`
      : undefined

    const factorSourceOptions = [
      { id: 'custom', name: 'Custom factor' },
      { id: 'defra', name: defraSourceName },
      { id: 'epd', name: 'EPD (Environmental Product Declaration)' },
    ]

    // Org goods record factors per single unit; the quantity row only appears for the
    // global-good forms and for legacy org goods whose stored basis is not 1 (read-only).
    const showDeclaredQuantity = superAdmin || globalGood || (isEditPage && formik.values.unitAmount != null && Number(formik.values.unitAmount) !== 1)

    return (
      <>
        {hasCategoryOptions !== null && !hasCategoryOptions &&
          <InformationBox
            icon={IconName.ErrorOutline}
            text={`You do not have any ${screenLabelsContext.getLabel('product')} categories`}
            actionText='Click here to create one'
            link={superAdmin ? `/super-admin/products?tabId=1&openDialog=true` : '/settings/company-settings/product-categories?openDialog=true'}
          />
        }
        {hasCategoryOptions !== null && hasCategoryOptions && categoryOptions &&
          <Box component='form' onSubmit={formik.handleSubmit}>
            <DefraFactorDialog open={defraOpen} onClose={() => setDefraOpen(false)} onSelect={handleDefraFactorSelect} />
            {categoryOptions &&
              <>
                <FormSelectInputDisplay
                  isEditPage={isEditPage}
                  editField={editField}
                  setEditField={setEditField}
                  allowEdit={true}
                  cancelEditHandler={cancelEditHandler}
                  disabled={isEditPage && editField !== 'category'}
                  isTopRow={true}
                  bottomBorder={false}
                  title='Category'
                  name='category'
                  value={category}
                  onChange={selectCategoryHandler}
                  array={categoryOptions}
                  hideEditIcon={!goodActive && !superAdmin}
                />
                {subCategoryOneOptions && subCategoryOneOptions.length > 0 &&
                  <FormSelectInputDisplay
                    isEditPage={isEditPage}
                    editField={editField}
                    setEditField={setEditField}
                    additionalText='Optional'
                    showAdditionalText={categoryOnly}
                    allowEdit={false}
                    disabled={isEditPage && editField !== 'category'}
                    topBorder={false}
                    bottomBorder={false}
                    forceOutlined={isEditPage && editField === 'category'}
                    title='Subcategory 1'
                    name='subCategoryOne'
                    cancelEditHandler={cancelEditHandler}
                    value={subCategoryOne}
                    onChange={selectSubCategoryOneHandler}
                    array={subCategoryOneOptions}
                    hideEditIcon={(!goodActive && !superAdmin) || isEditPage}
                  />
                }
                {subCategoryTwoOptions && subCategoryTwoOptions.length > 0 &&
                  <FormSelectInputDisplay
                    isEditPage={isEditPage}
                    editField={editField}
                    setEditField={setEditField}
                    additionalText='Optional'
                    showAdditionalText={categoryAndSubCategoryOneOnly}
                    allowEdit={false}
                    cancelEditHandler={cancelEditHandler}
                    disabled={isEditPage && editField !== 'category'}
                    topBorder={false}
                    bottomBorder={false}
                    forceOutlined={isEditPage && editField === 'category'}
                    title='Subcategory 2'
                    name='subCategoryTwo'
                    value={subCategoryTwo}
                    onChange={selectSubCategoryTwoHandler}
                    array={subCategoryTwoOptions}
                    hideEditIcon={(!goodActive && !superAdmin) || isEditPage}
                  />
                }
              </>}
            <FormTextInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'name'}
              title='Name'
              name='name'
              value={formik.values.name}
              touched={formik.touched.name}
              errors={formik.errors.name}
              hideEditIcon={!goodActive && !superAdmin}
              cancelEditHandler={cancelEditHandler}
              onChange={formik.handleChange}
              customErrorMessage={errorCode === 1 ? errorMessage : undefined}
            />
            <FormTextInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'capitalGoodReference'}
              title={`${screenLabelsContext.getLabel('product', { capitalize: 'first' })} Reference`}
              name='capitalGoodReference'
              value={formik.values.capitalGoodReference || ''}
              touched={formik.touched.capitalGoodReference}
              errors={formik.errors.capitalGoodReference}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              hideEditIcon={!goodActive && !superAdmin}
              tooltip='Enter multiple references separated by commas (e.g., REF1, REF2, REF3)'
            />
            <FormTextInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'modelNumber'}
              title='Model Number'
              name='modelNumber'
              value={formik.values.modelNumber}
              touched={formik.touched.modelNumber}
              errors={formik.errors.modelNumber}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              hideEditIcon={!goodActive && !superAdmin}
              customErrorMessage={errorCode === 2 ? errorMessage : undefined}
            />
            <FormTextInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'description'}
              title='Description'
              name='description'
              value={formik.values.description}
              touched={formik.touched.description}
              errors={formik.errors.description}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              hideEditIcon={!goodActive && !superAdmin}
            />
            {!(!superAdmin && globalGood) &&
              <FormImageInputDisplay
                isEditPage={isEditPage}
                editField={editField}
                setEditField={setEditField}
                disabled={isEditPage && editField !== 'capitalGoodImageFilePath'}
                title='Image'
                name='capitalGoodImageFilePath'
                value={formik.values.capitalGoodImageFilePath}
                onChange={() => { }}
                cancelEditHandler={cancelEditHandler}
                hideEditIcon={!goodActive && !superAdmin}
                uploadButtonText='Upload Image'
                fileData={imageFileData}
                setFileData={setImageFileData}
                onFileUploadSubmit={uploadGoodImageHandler}
                hideSaveButton={true}
                imageWidth={75}
                imageHeight={75}
              />}
            <FormRadioInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'typeOfGood'}
              cancelEditHandler={cancelEditHandler}
              title='Type of Good'
              name='typeOfGood'
              value={formik.values.typeOfGood}
              touched={formik.touched.typeOfGood}
              errors={formik.errors.typeOfGood}
              onChange={typeOfGoodHandler}
              options={[
                { value: 'purchasedGood', label: 'Purchased Good or Service' },
                { value: 'capitalGood', label: 'Capital Good' },
                // Sold products are org-level library records: global (super admin) goods stay
                // two-option, and a good with booked emissions cannot be re-tagged as sold
                // (the BE rejects it; hiding the option keeps the form honest).
                ...(superAdmin || globalGood || goodInUse ? [] : [{ value: 'soldProduct', label: 'Sold Product' }]),
              ]}
              hideEditIcon={!goodActive && !superAdmin}
            />
            <FormAutoCompleteInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              disabled={isEditPage && editField !== 'manufacturerId'}
              title={'Manufacturer'}
              tooltip={`Who makes this ${screenLabelsContext.getLabel('product')}. The manufacturer is the source of its emission factor or EPD`}
              name='manufacturerId'
              value={formik.values.manufacturerId ? (manufacturers.find(m => m.id === formik.values.manufacturerId) as IManufacturer | undefined) || '' : (formik.values.manufacturerName || '')}
              touched={formik.touched.manufacturerId || formik.touched.manufacturerName}
              errors={formik.errors.manufacturerId || formik.errors.manufacturerName}
              options={manufacturers.sort((a, b) => a.name.localeCompare(b.name))}
              optionLabelKey={'name'}
              onChange={handleManufacturerChange}
              onInputChange={handleManufacturerInputChange}
              cancelEditHandler={cancelEditHandler}
              hideEditIcon={!goodActive && !superAdmin}
              freeSolo
            />
            {!superAdmin && !globalGood &&
              <FormAutoCompleteInputDisplay
                isEditPage={isEditPage}
                editField={editField}
                setEditField={setEditField}
                disabled={isEditPage && editField !== 'contactSupplierIds'}
                title={screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' })}
                tooltip={`Who you purchase this ${screenLabelsContext.getLabel('product')} from. You can link more than one ${screenLabelsContext.getLabel('supplier')}`}
                name='contactSupplierIds'
                value={suppliers.filter(supplier => (formik.values.contactSupplierIds || []).includes(supplier.id))}
                touched={formik.touched.contactSupplierIds}
                errors={formik.errors.contactSupplierIds}
                options={sortedSuppliers}
                optionLabelKey={'name'}
                onChange={handleSuppliersChange}
                cancelEditHandler={cancelEditHandler}
                hideEditIcon={!goodActive}
                multiple
                virtualized
              />}
            {/* The declared-quantity input is gone for org goods: factors are per 1 unit (the
                engine has always computed factor x entered units). A legacy good whose stored
                basis is not 1 shows it read-only so its display stays true to its data. */}
            {showDeclaredQuantity &&
              <FormTextInputDisplay
                isEditPage={isEditPage}
                editField={editField}
                setEditField={setEditField}
                allowEdit={superAdmin || globalGood}
                disabled={(superAdmin || globalGood) ? ((isEditPage && (editField !== 'unitAmount' || isSupplierVerified)) || factorSourceChoice === 'defra') : true}
                title='Declared quantity'
                tooltip={(superAdmin || globalGood)
                  ? (factorSourceChoice === 'defra' ? 'Set by the chosen library factor' : undefined)
                  : 'Legacy factor basis. New factors are recorded per single unit'}
                name='unitAmount'
                type='number'
                bottomBorder={false}
                value={formik.values.unitAmount ?? ''}
                touched={formik.touched.unitAmount}
                errors={formik.errors.unitAmount}
                onChange={formik.handleChange}
                cancelEditHandler={cancelEditHandler}
                customErrorMessage={errorCode === 7 ? errorMessage : undefined}
                hideEditIcon={(!goodActive && !superAdmin) || isSupplierVerified || (!superAdmin && !globalGood)}
              />}
            {measurementUnits &&
              <FormSelectInputDisplay
                isEditPage={isEditPage}
                editField={editField}
                setEditField={setEditField}
                allowEdit={true}
                disabled={(isEditPage && (editField !== 'unitType' || isSupplierVerified)) || factorSourceChoice === 'defra'}
                title={showDeclaredQuantity ? '' : 'Unit'}
                name='unitType'
                topBorder={!showDeclaredQuantity}
                value={formik.values.unitType ?? ''}
                touched={formik.touched.unitType}
                errors={formik.errors.unitType}
                onChange={formik.handleChange}
                cancelEditHandler={cancelEditHandler}
                array={measurementUnits.sort((a, b) => a.name.localeCompare(b.name))}
                valueKey={'id'}
                labelKey={'name'}
                customErrorMessage={errorCode === 6 ? errorMessage : undefined}
                hideEditIcon={(!goodActive && !superAdmin) || isSupplierVerified}
              />}
            {!superAdmin && !globalGood &&
              <FormSelectInputDisplay
                isEditPage={isEditPage}
                editField={editField}
                setEditField={setEditField}
                allowEdit={true}
                disabled={isEditPage && editField !== 'factorSourceChoice'}
                title='Emission factor source'
                name='factorSourceChoice'
                value={factorSourceChoice}
                onChange={(e: SelectChangeEvent<string>) => handleSourceChange(e.target.value)}
                cancelEditHandler={cancelEditHandler}
                array={factorSourceOptions}
                valueKey={'id'}
                labelKey={'name'}
                hideEditIcon={!goodActive && !superAdmin}
              />}
            {/* Order: source -> factor -> value. The "Emission factor" is the selected factor:
                read-only for library/EPD sources (chosen via the picker, not typed); free text only for Custom. */}
            {/* For DEFRA/EPD the factor is read-only and the row's edit pencil opens the picker
                (no separate button); free text + inline edit only for Custom. */}
            <FormTextInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              disabled={(isEditPage && (editField !== 'sourceOfEmission' || isSupplierVerified)) || factorSourceChoice !== 'custom'}
              title='Emission factor'
              name='sourceOfEmission'
              value={isSupplierVerified ? '' : formik.values.sourceOfEmission}
              touched={formik.touched.sourceOfEmission}
              errors={formik.errors.sourceOfEmission}
              hideEditIcon={!goodActive && !superAdmin}
              cancelEditHandler={cancelEditHandler}
              onChange={formik.handleChange}
              onEditClick={factorSourceChoice === 'defra' ? openDefraPicker : factorSourceChoice === 'epd' ? () => onManageEpd && onManageEpd() : undefined}
              endAdornment={factorSourceChoice === 'defra' && !isSupplierVerified ? (
                <InputAdornment position="end">
                  <Button variant='text' size='small' onClick={openDefraPicker} startIcon={<LibraryBooksOutlinedIcon />} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                    {formik.values.kgCarbonEmission ? 'Change factor' : 'Choose factor'}
                  </Button>
                </InputAdornment>
              ) : undefined}
              chip={isSupplierVerified ? 'Verified by Supplier/Manufacturer' : undefined}
              chipColor='success'
            />
            {!superAdmin && !globalGood && factorSourceChoice === 'epd' && !isEditPage &&
              <Typography variant='body2' color='text.secondary' sx={{ pl: { xs: 3, md: 'calc(40% + 32px)' }, mt: -0.5, mb: 1 }}>
                Save the {screenLabelsContext.getLabel('product')} first, then you'll be prompted to attach the EPD document.
              </Typography>
            }
            {!superAdmin && !globalGood && factorSourceChoice === 'defra' && !formik.values.kgCarbonEmission &&
              <Typography variant='body2' color='text.secondary' sx={{ pl: { xs: 3, md: 'calc(40% + 32px)' }, mt: -0.5, mb: 1 }}>
                No library factor chosen yet. Choose one, or the {screenLabelsContext.getLabel('product')} saves without a factor.
              </Typography>
            }
            <FormTextInputDisplay
              isEditPage={isEditPage}
              editField={editField}
              setEditField={setEditField}
              allowEdit={true}
              isBottomRow={!hasStageSection}
              disabled={(isEditPage && (editField !== 'kgCarbonEmission' || isSupplierVerified)) || factorSourceChoice !== 'custom'}
              title='Emissions per Unit'
              name='kgCarbonEmission'
              type='number'
              value={formik.values.kgCarbonEmission ?? ''}
              touched={formik.touched.kgCarbonEmission}
              errors={formik.errors.kgCarbonEmission}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              customErrorMessage={errorCode === 5 ? errorMessage : undefined}
              endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>kg CO<sub>2</sub>e</Typography></InputAdornment>}
              hideEditIcon={(!goodActive && !superAdmin) || isSupplierVerified}
            />
            {/* Life-cycle stage figures: transcribed from external LCA/EPD reports, display-only.
                Collapsed by default (most products never record them); opens automatically when
                values exist. The header is a row of the bordered list, so folding it away keeps
                the form tidy. */}
            {hasStageSection && (
              <FormSectionToggleRow
                title='Life-cycle stage figures'
                caption={viewStageFigures ? undefined : (recordedStageCount > 0 ? `${recordedStageCount} recorded` : 'Optional')}
                open={viewStageFigures}
                onToggle={() => setViewStageFigures(!viewStageFigures)}
                isBottomRow={!viewStageFigures && !hasCustomFieldsSection}
              />
            )}
            {hasStageSection && viewStageFigures && STAGE_FIGURE_FIELDS.map(({ field: stageField, label: stageLabel }, index) => (
              <FormTextInputDisplay
                key={stageField}
                isEditPage={isEditPage}
                editField={editField}
                setEditField={setEditField}
                isBottomRow={index === STAGE_FIGURE_FIELDS.length - 1 && !hasCustomFieldsSection}
                disabled={isEditPage && editField !== stageField}
                title={stageLabel}
                name={stageField}
                type='number'
                value={(formik.values as any)[stageField] ?? ''}
                touched={(formik.touched as any)[stageField]}
                errors={(formik.errors as any)[stageField]}
                onChange={formik.handleChange}
                cancelEditHandler={cancelEditHandler}
                tooltip={index === 0 ? 'Optional: transcribe the per-module GWP values stated in the product\'s LCA report or EPD, in kg CO2e per unit. They are stored as entered and never totalled or used in calculations.' : undefined}
                helperText={stageField === 'stageA1A3' ? stageA1A3MismatchWarning : undefined}
                endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>kg CO<sub>2</sub>e</Typography></InputAdornment>}
                hideEditIcon={!goodActive && !superAdmin}
              />
            ))}
            {/* Organisation-defined custom fields (including the passport attribute set) fold
                the same way, so the form's core stays short. Opens itself when any field is
                mandatory or already filled in. */}
            {hasCustomFieldsSection && (
              <FormSectionToggleRow
                title='Additional details'
                caption={viewCustomFields ? undefined : (recordedCustomFieldCount > 0 ? `${recordedCustomFieldCount} recorded` : 'Optional')}
                open={viewCustomFields}
                onToggle={() => setViewCustomFields(!viewCustomFields)}
                isBottomRow={!viewCustomFields}
              />
            )}
            {hasCustomFieldsSection && viewCustomFields && customFields.map((field, index) => (
              <FormTextInputDisplay
                key={field.id}
                isBottomRow={index === customFields.length - 1}
                isEditPage={isEditPage}
                editField={editField}
                setEditField={setEditField}
                disabled={isEditPage && editField !== field.id}
                title={field.fieldName}
                name={field.id}
                type={field.fieldType === 'number' ? 'number' as const : 'text' as const}
                value={(formik.values as any)[field.id]}
                touched={(formik.touched as any)[field.id]}
                errors={(formik.errors as any)[field.id]}
                onChange={formik.handleChange}
                cancelEditHandler={cancelEditHandler}
                hideEditIcon={!goodActive && !superAdmin}
              />
            ))
            }
            {!isEditPage && <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
              <DialogSaveButtons
                isForm={true}
                disableSaveButton={!formik.values.goodCategoryId}
                isSubmitting={isSubmitting}
                cancelHandler={() => navigate(superAdmin ? `/super-admin/products` : `/organisation/products`)}
                submitHandler={formik.submitForm}
              />
            </Stack>}
          </Box >}
      </>
    )
  }